"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { 
  Upload, 
  FileText, 
  Image as ImageIcon, 
  X, 
  CheckCircle, 
  Download,
  User,
  GraduationCap,
  Briefcase,
  Camera,
  Shield
} from "lucide-react";
import openTurfLogo from '../assets/OpenTurf-logo.png';

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  type: string;
}

interface DocumentCollectionProps {
  onComplete?: () => void;
  onShowTerms?: () => void;
}

const documentSteps = [
  {
    id: 1,
    title: "Identity Documents",
    description: "Let's start with your identity. Please upload your Aadhar, PAN, or Passport.",
    icon: User,
    acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    maxFiles: 3,
    examples: ["Aadhar Card", "PAN Card", "Passport"]
  },
  {
    id: 2,
    title: "Resume",
    description: "Now, your resume. Help us get to know you better.",
    icon: FileText,
    acceptedTypes: ".pdf,.doc,.docx",
    maxFiles: 1,
    examples: ["Current Resume", "CV"]
  },
  {
    id: 3,
    title: "Educational Certificates",
    description: "Time to upload your educational certificates. Include 10th, 12th/diploma, and your college degrees.",
    icon: GraduationCap,
    acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    maxFiles: 5,
    examples: ["10th Certificate", "12th/Diploma", "Degree Certificates"]
  },
  {
    id: 4,
    title: "Work Documents",
    description: "If you've worked before, we'll need your last offer letter and payslips.",
    icon: Briefcase,
    acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    maxFiles: 4,
    examples: ["Offer Letter", "Payslips", "Experience Letter"]
  },
  {
    id: 5,
    title: "Professional Photo",
    description: "Almost done! Upload a professional photo of yourself.",
    icon: Camera,
    acceptedTypes: ".jpg,.jpeg,.png",
    maxFiles: 1,
    examples: ["Professional headshot", "Passport size photo"]
  },
  {
    id: 6,
    title: "NDA Document",
    description: "Download the NDA, sign it, and upload the scanned copy here.",
    icon: Shield,
    acceptedTypes: ".pdf,.jpg,.jpeg,.png",
    maxFiles: 1,
    examples: ["Signed NDA"],
    hasDownload: true
  }
];

export function DocumentCollection({ onComplete, onShowTerms }: DocumentCollectionProps) {
  const [currentStep, setCurrentStep] = React.useState(1);
  const [uploadedFiles, setUploadedFiles] = React.useState<Record<number, UploadedFile[]>>({});
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const currentStepData = documentSteps.find(step => step.id === currentStep)!;
  const progress = (currentStep / documentSteps.length) * 100;

  const handleFileUpload = (files: FileList | null, stepId: number) => {
    if (!files) return;

    const step = documentSteps.find(s => s.id === stepId)!;
    const currentFiles = uploadedFiles[stepId] || [];
    
    if (currentFiles.length >= step.maxFiles) {
      alert(`Maximum ${step.maxFiles} files allowed for this step.`);
      return;
    }

    const newFiles: UploadedFile[] = [];
    const remainingSlots = step.maxFiles - currentFiles.length;
    
    for (let i = 0; i < Math.min(files.length, remainingSlots); i++) {
      const file = files[i];
      const fileId = `${stepId}-${Date.now()}-${i}`;
      
      newFiles.push({
        id: fileId,
        file,
        preview: URL.createObjectURL(file),
        type: file.type
      });
    }

    setUploadedFiles(prev => ({
      ...prev,
      [stepId]: [...currentFiles, ...newFiles]
    }));
  };

  const removeFile = (stepId: number, fileId: string) => {
    setUploadedFiles(prev => ({
      ...prev,
      [stepId]: (prev[stepId] || []).filter(file => file.id !== fileId)
    }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files, currentStep);
  };

  const handleNext = () => {
    if (currentStep < documentSteps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete?.();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = (uploadedFiles[currentStep] || []).length > 0;

  const handleDownloadNDA = () => {
    // In a real app, this would download the actual NDA
    const link = document.createElement('a');
    link.href = 'data:text/plain;charset=utf-8,Sample NDA Document - Please print, sign, and upload the scanned copy.';
    link.download = 'NDA_Template.txt';
    link.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-purple-100/10 to-transparent rounded-full" />
      </div>

      {/* Header with Progress */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/50 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
            <div className="text-sm text-gray-600">
              Step {currentStep} of {documentSteps.length}
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Document Collection</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="relative z-10"
            >
              {/* Step Header */}
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="mb-6"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl mx-auto flex items-center justify-center mb-4">
                    <currentStepData.icon className="w-8 h-8 text-white" />
                  </div>
                </motion.div>
                
                <motion.h1 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="mb-4 text-gray-900"
                >
                  {currentStepData.description}
                </motion.h1>
                
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="flex flex-wrap justify-center gap-2"
                >
                  {currentStepData.examples.map((example, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                    >
                      {example}
                    </span>
                  ))}
                </motion.div>
              </div>

              {/* Upload Area */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20"
              >
                {/* Download NDA Button */}
                {currentStepData.hasDownload && (
                  <div className="mb-6 text-center">
                    <Button
                      onClick={handleDownloadNDA}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download NDA Template
                    </Button>
                    <p className="text-sm text-gray-600 mt-2">
                      Please print, sign, and upload the scanned copy below.
                    </p>
                  </div>
                )}

                {/* Drag and Drop Area */}
                <div
                  className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                    dragOver 
                      ? 'border-purple-400 bg-purple-50' 
                      : 'border-gray-300 hover:border-purple-300'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-700 mb-2">
                    Drag and drop your files here, or{" "}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-purple-600 hover:text-purple-700 underline"
                    >
                      browse
                    </button>
                  </p>
                  <p className="text-sm text-gray-500">
                    Maximum {currentStepData.maxFiles} file(s) • Accepted formats: {currentStepData.acceptedTypes}
                  </p>
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple={currentStepData.maxFiles > 1}
                    accept={currentStepData.acceptedTypes}
                    onChange={(e) => handleFileUpload(e.target.files, currentStep)}
                    className="hidden"
                  />
                </div>

                {/* File Previews */}
                {(uploadedFiles[currentStep] || []).length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-gray-900 mb-4">Uploaded Files</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {(uploadedFiles[currentStep] || []).map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex-shrink-0">
                            {file.type.startsWith('image/') ? (
                              <img
                                src={file.preview}
                                alt={file.file.name}
                                className="w-12 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-purple-100 rounded flex items-center justify-center">
                                <FileText className="w-6 h-6 text-purple-600" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 truncate">
                              {file.file.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(file.file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <button
                            onClick={() => removeFile(currentStep, file.id)}
                            className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8">
                  <Button
                    variant="ghost"
                    onClick={handlePrevious}
                    disabled={currentStep === 1}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  >
                    Previous
                  </Button>
                  
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed}
                    className="bg-purple-600 hover:bg-purple-700 text-white disabled:bg-gray-300"
                  >
                    {currentStep === documentSteps.length ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Complete
                      </>
                    ) : (
                      'Next Step'
                    )}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}